/**
 * Squish Deli cart extras helper (snippets/sd-cart-extras.liquid). BUILD A PLATE + CART role.
 *
 * The extras are plain Liquid and Horizon re-renders them with every cart change, so this module only
 * re-applies the date-dependent bits in the browser: the arrival range ([data-sd-delivery], via sd-ui) and the
 * Christmas line ([data-sd-xmas]). That keeps cached HTML honest and makes ?sd_date=YYYY-MM-DD QA work.
 * It also copies the free-Mini badge (hidden <div data-sd-cx-mini-badge data-line-key data-free>) into the Mini's own
 * cart line, so the word "free" sits next to the $5.00 it refers to (FTC Free Guide), and removes it when the offer
 * stops applying. The source is a plain div (not a <template>) because Horizon's morph updates a div's children but
 * never a template's .content. Runs once per page (module scripts with the same URL execute once) and watches for morphed cart markup.
 */
import { nyToday, renderDelivery } from '@theme/sd-ui';

/** BUILD-SPEC 5 phases (same dates as snippets/sd-xmas-line.liquid and assets/sd-builder.js). */
function xmasPhase(t) {
  if (t <= '2026-11-22') return 'pre';
  if (t <= '2026-11-24') return 'risk';
  if (t <= '2026-12-24') return 'late';
  return 'none';
}

function apply(root) {
  if (!(root instanceof HTMLElement) || root.dataset.sdCxDone === '1') return;
  root.dataset.sdCxDone = '1';
  const phase = xmasPhase(nyToday().toISOString().slice(0, 10));
  for (const wrap of root.querySelectorAll('[data-sd-xmas-wrap]')) {
    if (wrap instanceof HTMLElement) wrap.hidden = phase === 'none';
    for (const line of wrap.querySelectorAll('[data-sd-xmas]')) {
      if (line instanceof HTMLElement) line.hidden = line.getAttribute('data-sd-xmas') !== phase;
    }
  }
  renderDelivery(root);
}

/** The cart row for a line key, searched from the badge source outwards (drawer and /cart page each find their own). */
function rowFor(source, key) {
  const selector = `[data-key="${CSS.escape(key)}"]:not([data-sd-cx-mini-badge])`;
  for (let node = source.parentElement; node; node = node.parentElement) {
    const row = node.querySelector(selector);
    if (row) return row;
  }
  return null;
}

function placeMiniBadges() {
  const sources = [...document.querySelectorAll('[data-sd-cx-mini-badge]')];
  const wanted = new Set();
  for (const source of sources) {
    if (!(source instanceof HTMLElement)) continue;
    const key = source.dataset.lineKey || '';
    const row = key && rowFor(source, key);
    if (!row) continue;
    // The badge is its own cell of the row (a direct grid item), so it spans the line under the price.
    const host = row;
    const free = source.dataset.free === 'true';
    wanted.add(host);
    const placed = host.querySelector(':scope > [data-sd-cx-mini-placed]');
    // Same state and text already there: leave it (keeps the observer from looping).
    if (
      placed instanceof HTMLElement &&
      placed.dataset.sdCxMiniPlaced === key &&
      placed.dataset.free === String(free) &&
      placed.textContent === source.textContent
    ) {
      continue;
    }
    placed?.remove();
    const cell = document.createElement(row.tagName === 'TR' ? 'td' : 'div');
    cell.className = 'sd-cx-mini-cell';
    cell.setAttribute('role', 'cell');
    cell.dataset.sdCxMiniPlaced = key;
    cell.dataset.free = String(free);
    for (const child of source.childNodes) cell.append(child.cloneNode(true));
    host.append(cell);
  }
  for (const badge of document.querySelectorAll('[data-sd-cx-mini-placed]')) {
    const host = badge.parentElement;
    if (!host || !wanted.has(host)) badge.remove();
  }
}

/**
 * Cart page, phones: the fixed "Total · Check out" bar (sections/main-cart.liquid) hides while the real summary's
 * Check out button is on screen or above it, so there is never a second checkout button in view.
 */
let barIo = null;
let barTarget = null;
function syncCartBar() {
  const bar = document.querySelector('[data-sd-cart-bar]');
  if (!(bar instanceof HTMLElement) || !barTarget) return;
  const hide = barTarget.getBoundingClientRect().top < window.innerHeight;
  if (bar.classList.contains('is-hidden') !== hide) bar.classList.toggle('is-hidden', hide);
  if (bar.inert !== hide) bar.inert = hide;
}

function watchCartBar() {
  const bar = document.querySelector('[data-sd-cart-bar]');
  const target = bar ? document.querySelector('.cart-page__summary .cart__ctas, .cart-page__summary') : null;
  if (target === barTarget) return syncCartBar();
  barIo?.disconnect();
  barIo = null;
  barTarget = target;
  if (!(bar instanceof HTMLElement) || !target || !('IntersectionObserver' in window)) return;
  barIo = new IntersectionObserver(syncCartBar);
  barIo.observe(target);
  syncCartBar();
}

function scan() {
  for (const root of document.querySelectorAll('[data-sd-cx]')) apply(root);
  placeMiniBadges();
  watchCartBar();
}

if (!window.SquishDeliCartExtras) {
  window.SquishDeliCartExtras = { scan };
  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(scan);
  };
  // Horizon morphs the drawer / cart page in place: new or reset extras lose data-sd-cx-done.
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-sd-cx-done'],
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
}
